import { useEffect, useState } from "react";
import { ContenedorPageTemplate } from "../../../components/templates/ContenedorPageTemplate";
import { getAllEstudiantes } from "../../../services/estudiante.service";
import { getAllRegistros } from "../../../services/registro.service";
import { getAllRegistrosV2 } from "../../../services/registroV2.service";
import { getAllGrupos } from "../../../services/grupo.service";
import { getAllPaises } from "../../../services/pais.service";

import { EstudianteList } from "../../../modules/estudiante/EstudianteList";
import { TITULO_EST_LIST, generosPersona } from "../../../utils/constantes";
import { obtenerCodigoRegion } from "../../../utils/codigoRegionHelper";

export const EstudiantePage = () => {
  const [estudiantes, setEstudiantes] = useState(null);
  const [paisesCache, setPaisesCache] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [filtroGenero, setFiltroGenero] = useState("Todos");
  const DIR_ICONO_EST = "/img/iconos/estudiante_que_va_en_medio.svg";

  useEffect(() => {
    obtenerEstudiantes();
  }, []);

  const obtenerEstudiantes = async () => {
    try {
      const [estudiantes, registrosV1, registrosV2, gruposAll, paises] = await Promise.all([
        getAllEstudiantes(),
        getAllRegistros(),
        getAllRegistrosV2().catch(() => []),
        getAllGrupos().catch(() => []),
        getAllPaises().catch(() => []),
      ]);     

      // Combinar registros priorizando V2
      const registroPorEstudiante = {};
      
      // Primero V1
      for (const reg of registrosV1) {
        const idEst = reg.idEstudiante?.trim();
        if (idEst) registroPorEstudiante[idEst] = reg;
      }
      
      // Luego V2 (sobrescribe V1 si existe en ambos)
      for (const reg of registrosV2) {
        const idEst = reg.idEstudiante?.trim();
        if (idEst) {
          registroPorEstudiante[idEst] = {
            ...reg,
            idRegistro: reg.idRegistroNotasV2, // Normalizar nombre de ID
            estado: reg.estadoV2,
            notaFinal: reg.notaFinal
          };
        }
      }
      const gruposCache = Object.create(null);
      for (const g of gruposAll || []) {
        if (g?.idGrupo) gruposCache[g.idGrupo] = g;
      }
      const isHabilitadoEstudiante = (est) => {
        const raw = est?.estudiante?.habilitado;
        if (raw === true) return true;
        if (raw === false) return false;
        const v = String(raw ?? "").trim().toUpperCase();
        if (["1", "TRUE", "T", "SI", "S", "HABILITADO"].includes(v)) return true;
        if (["0", "FALSE", "F", "NO", "N", "INHABILITADO"].includes(v)) return false;
        return false;
      };

      const estudiantesConEstado = estudiantes.map((est) => {
        const idEst = est.estudiante?.idEstudiante?.trim();
        const reg = registroPorEstudiante[idEst]; // Esto ya trae el prioritario (V2 sobre V1)

        const tipoCongelacion = reg?.tipoCongelacion?.trim()?.toLowerCase();
        const tipoFinal =
          tipoCongelacion === "voluntario" || tipoCongelacion === "institucional"
            ? tipoCongelacion
            : "N/A";
        const tipoAdmisionNombre = est.tipoAdmision?.nombre || est?.estudiante?.tipoAdmision || "N/A";

        let grupo = reg?.grupo;
        const idGrupo = reg?.idGrupo;
        if (!grupo && idGrupo) {
          grupo = gruposCache[idGrupo] || null;
        }

        const habilitadoBool = isHabilitadoEstudiante(est);

        return {
          ...est,
          estudiante: {
            ...est.estudiante,
            tipoCongelacion: tipoFinal,
            tipoAdmision: tipoAdmisionNombre,
          },
          registro: reg ? { ...reg, grupo } : null,
          _habilitadoBool: habilitadoBool,
        };
      });

      const soloHabilitados = estudiantesConEstado.filter((e) => e._habilitadoBool === true);   
      setPaisesCache(paises || []);      
      soloHabilitados.forEach(est => {
        est.paisesCache = paises;
      });
      
      setEstudiantes(soloHabilitados);
    } catch (err) {
      console.error("Error al cargar estudiantes o registros:", err);
    }
  };

  const handleDelete = (idEstudiante) => {
    setEstudiantes((prevEstudiantes) =>
      prevEstudiantes.filter((est) => est.estudiante.idEstudiante !== idEstudiante)
    );
  };

  const normalizarTexto = (texto) => {
    if (!texto) return "";
    return texto
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") 
      .toLowerCase()
      .trim();
  };
  
  const estudiantesFiltrados = estudiantes?.filter(({ persona, estudiante: est, usuario, departamento }) => {
    const textoBusqueda = normalizarTexto(busqueda);
    const generoPersona = (persona?.sexo ?? "").trim().toLowerCase();
    const generoSeleccionado = filtroGenero.trim().toLowerCase();
    if (generoSeleccionado !== "todos" && generoPersona !== generoSeleccionado) {
      return false;
    }
    const codigoRegion = obtenerCodigoRegion(persona, departamento, paisesCache);

    const campos = [
      usuario?.username ?? "",      
      persona?.nombre,
      persona?.apellido,
      persona?.ci,
      persona?.sexo,
      persona?.celular,
      typeof codigoRegion === 'string' ? codigoRegion : 'N/A',
      est?.tipoAdmision,
      est?.credito?.toString(),
    ];

    const textoCompleto = campos.map(normalizarTexto).join(" ");
    return textoCompleto.includes(textoBusqueda);
  });
  
  return (
    <ContenedorPageTemplate
      objetos={estudiantes}
      dirIcono={DIR_ICONO_EST}
      titulo={TITULO_EST_LIST}
    >
      <div className="flex flex-col justify-start items-start">
        <p>
          Total de estudiantes: <span>{estudiantesFiltrados?.length}</span>
        </p>
      </div>
      <div className="flex flex-wrap gap-2 py-3">
        {["Todos", ...generosPersona.map((g) => g.value)].map((genero) => {
          const activo = filtroGenero === genero;
          return (
            <button
              key={genero}
              type="button"
              onClick={() => setFiltroGenero(genero)}
              className={`px-4 py-2 rounded-xl border transition-colors ${
                activo
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-slate-700 border-slate-200 hover:border-blue-400 hover:text-blue-600"
              }`}
            >
              {genero}
            </button>
          );
        })}
      </div>
      <div className="flex gap-4 py-3">
        <input
          type="text"
          placeholder="Buscar por nombre, apellido, código, Teléfono, código región, tipo de admisión, CI, Género o crédito"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          className="flex-1 text-black border shadow-sm rounded-xl py-3 px-4 focus:ring-1 focus:border-blue-500 focus:ring-blue-300 transition-colors hover:border-blue-500 border-slate-200"
        />
      </div>
      <EstudianteList estudiantes={estudiantesFiltrados} onDelete={handleDelete} />
    </ContenedorPageTemplate>
  );
};
 